import { useRef, useEffect } from "react";
import { motion, useInView } from "framer-motion";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { ChatbotWidget } from "@/components/ChatbotWidget";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { insertContactRequestSchema } from "@shared/schema";
import type { InsertContactRequest } from "@shared/schema";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Shield,
  FileCheck,
  AlertTriangle,
  CheckCircle2,
  ClipboardCheck,
  ArrowRight,
  Phone,
  Mail,
  X,
  ChevronRight,
  Search,
  FileWarning,
  FileMinus,
  UserX,
  BarChart3,
  Users,
  FileText,
  Target,
  Layers,
  Clock,
  Eye,
  BookOpen,
  Menu,
  BadgeCheck,
  Scale,
  Briefcase,
  Lock,
  Download,
} from "lucide-react";
import { useState } from "react";
import logoFullColor from "@assets/MAPA35_logo_fullcolor_small.webp";
import logoWhite from "@assets/MAPA35_logo_white_transparent_1771009903875.webp";
import logoIcon from "@assets/MAPA35_icon_fullcolor_small.webp";
import heroBgPattern from "@assets/MAPA35_pattern_dark_small.webp";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
    Calendly?: {
      initInlineWidget: (opts: {
        url: string;
        parentElement: HTMLElement;
        prefill?: Record<string, unknown>;
        utm?: Record<string, unknown>;
      }) => void;
    };
  }
}

const CALENDLY_URL = "https://calendly.com/admin-concilya/consulta-nom-035-mapa35";

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "gclid"] as const;

function captureTrackingParams() {
  try {
    const params = new URLSearchParams(window.location.search);
    for (const key of UTM_KEYS) {
      const val = params.get(key);
      if (val) sessionStorage.setItem(key, val);
    }
  } catch {}
}

export function getTrackingParams(): Partial<Record<typeof UTM_KEYS[number], string>> {
  try {
    const params = new URLSearchParams(window.location.search);
    const result: Partial<Record<typeof UTM_KEYS[number], string>> = {};
    for (const key of UTM_KEYS) {
      const val = sessionStorage.getItem(key) || params.get(key);
      if (val) result[key] = val;
    }
    return result;
  } catch {
    return {};
  }
}

const GA_ID = "G-8VLF1C7LL6";
const ADS_ID = "AW-17706966381";

export function trackEvent(eventName: string, params?: Record<string, string>) {
  if (typeof window.gtag === "function") {
    window.gtag("event", eventName, { send_to: GA_ID, ...params });
    window.gtag("event", eventName, { send_to: ADS_ID, ...params });
  }
}

function CalendlyModal({ onClose, prefill }: { onClose: () => void; prefill?: { name?: string; email?: string; phone?: string; company?: string } }) {
  useEffect(() => {
    const handleMessage = (e: MessageEvent) => {
      if (e.data?.event === "calendly.event_scheduled") {
        trackEvent("schedule_consultation", { event_category: "calendly", event_label: "consulta_agendada" });
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  const tracking = getTrackingParams();
  const calendlyParams = new URLSearchParams({
    embed_type: "Inline",
    hide_gdpr_banner: "1",
    primary_color: "0b1f3b",
    ...(tracking.utm_source && { utm_source: tracking.utm_source }),
    ...(tracking.utm_medium && { utm_medium: tracking.utm_medium }),
    ...(tracking.utm_campaign && { utm_campaign: tracking.utm_campaign }),
    ...(tracking.utm_term && { utm_term: tracking.utm_term }),
    ...((tracking.utm_content || tracking.gclid) && {
      utm_content: tracking.utm_content ?? tracking.gclid!,
    }),
    ...(prefill?.name && { name: prefill.name }),
    ...(prefill?.email && { email: prefill.email }),
    ...(prefill?.phone && { a1: prefill.phone }),
    ...(prefill?.company && { a2: prefill.company }),
  });
  const src = `${CALENDLY_URL}?${calendlyParams.toString()}`;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-background rounded-xl shadow-2xl overflow-hidden flex flex-col" style={{ height: "min(660px, 90vh)" }}>
        <div className="flex items-center justify-between px-5 py-4 border-b flex-shrink-0">
          <div>
            <p className="font-serif font-semibold text-sm text-foreground">Agenda tu consulta gratuita</p>
            <p className="text-xs text-muted-foreground">Selecciona el horario que mejor te convenga</p>
          </div>
          <Button size="icon" variant="ghost" onClick={onClose} data-testid="button-close-calendly" aria-label="Cerrar">
            <X className="w-4 h-4" />
          </Button>
        </div>
        <iframe
          src={src}
          className="flex-1 w-full border-0"
          title="Agendar consulta MAPA35"
        />
      </div>
    </div>
  );
}

function AnimatedSection({ children, className = "", delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
      transition={{ duration: 0.5, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showCalendly, setShowCalendly] = useState(false);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMobileOpen(false);
  };

  return (
    <>
    <nav className="fixed top-0 left-0 right-0 z-[9999] bg-background/90 backdrop-blur-lg border-b border-border/40">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-4 h-16">
          <button onClick={() => scrollTo("hero")} className="flex items-center flex-shrink-0" data-testid="link-logo">
            <img src={logoFullColor} alt="MAPA35" className="h-9 w-auto" width={255} height={72} />
          </button>

          <div className="hidden xl:flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={() => scrollTo("contacto")} data-testid="button-nav-contacto">
                Solicitar diagnóstico
              </Button>
              <Button size="sm" onClick={() => setShowCalendly(true)} data-testid="button-nav-calendly">
                Agendar cita
              </Button>
            </div>
            <button onClick={() => scrollTo("problema")} className="text-sm text-muted-foreground transition-colors" data-testid="link-nav-problema">Riesgos</button>
            <button onClick={() => scrollTo("promesa")} className="text-sm text-muted-foreground transition-colors" data-testid="link-nav-promesa">Solución</button>
            <button onClick={() => scrollTo("diferenciacion")} className="text-sm text-muted-foreground transition-colors" data-testid="link-nav-diferenciacion">Diferenciador</button>
            <button onClick={() => scrollTo("proceso")} className="text-sm text-muted-foreground transition-colors" data-testid="link-nav-proceso">Proceso</button>
            <Link href="/faq" className="text-sm text-muted-foreground transition-colors" data-testid="link-nav-faq">FAQ</Link>
            <Link href="/glosario" className="text-sm text-muted-foreground transition-colors" data-testid="link-nav-glosario">Glosario</Link>
            <Link href="/blog" className="text-sm text-muted-foreground transition-colors" data-testid="link-nav-blog">Blog</Link>

            <div className="flex items-center gap-2">
              <Button size="sm" asChild>
                <a href="https://app.mapa35.com/" target="_blank" rel="noopener noreferrer" data-testid="button-nav-login">
                  Iniciar Sesión
                </a>
              </Button>
              <Button size="sm" asChild>
                <a href="https://app.mapa35.com/#/onboarding" target="_blank" rel="noopener noreferrer" data-testid="button-nav-register">
                  Registrarse
                </a>
              </Button>
            </div>
          </div>
          <Button size="icon" variant="ghost" className="xl:hidden" onClick={() => setMobileOpen(!mobileOpen)} data-testid="button-mobile-menu" aria-label={mobileOpen ? "Cerrar menú" : "Abrir menú"}>
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>
      </div>

      {mobileOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          className="xl:hidden border-t bg-background"
        >
          <div className="px-4 py-4 flex flex-col gap-1">
            <button onClick={() => scrollTo("problema")} className="text-sm text-muted-foreground text-left py-2.5 px-3 rounded-md" data-testid="link-mobile-problema">Riesgos</button>
            <button onClick={() => scrollTo("promesa")} className="text-sm text-muted-foreground text-left py-2.5 px-3 rounded-md" data-testid="link-mobile-promesa">Solución</button>
            <button onClick={() => scrollTo("diferenciacion")} className="text-sm text-muted-foreground text-left py-2.5 px-3 rounded-md" data-testid="link-mobile-diferenciacion">Diferenciador</button>
            <button onClick={() => scrollTo("proceso")} className="text-sm text-muted-foreground text-left py-2.5 px-3 rounded-md" data-testid="link-mobile-proceso">Proceso</button>
            <Link href="/faq" className="text-sm text-muted-foreground text-left py-2.5 px-3 rounded-md" data-testid="link-mobile-faq">FAQ</Link>
            <Link href="/glosario" className="text-sm text-muted-foreground text-left py-2.5 px-3 rounded-md" data-testid="link-mobile-glosario">Glosario</Link>
            <Link href="/blog" className="text-sm text-muted-foreground text-left py-2.5 px-3 rounded-md" data-testid="link-mobile-blog">Blog</Link>
            <Button size="sm" onClick={() => scrollTo("contacto")} className="w-full mt-3" data-testid="button-mobile-contacto">
              Solicitar diagnóstico
            </Button>
          </div>
        </motion.div>
      )}
    </nav>
    {showCalendly && <CalendlyModal onClose={() => setShowCalendly(false)} />}
    </>
  );
}

function HeroSection() {
  const scrollToContact = (label: string) => {
    trackEvent("click_cta", { event_category: "hero", event_label: label });
    document.getElementById("contacto")?.scrollIntoView({ behavior: "smooth" });
    setTimeout(() => {
      const nameInput = document.querySelector('[data-testid="input-name"]') as HTMLInputElement | null;
      nameInput?.focus();
    }, 600);
  };

  return (
    <section id="hero" className="relative min-h-[92vh] flex items-center pt-16">
      <div className="absolute inset-0">
        <img
          src={heroBgPattern}
          alt="MAPA35 patrón corporativo"
          className="w-full h-full object-cover"
          width={1280}
          height={720}
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1F3B]/90 via-[#0B1F3B]/70 to-[#0B1F3B]/50" />
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
        <div className="max-w-2xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <div className="flex items-center gap-4 mb-3">
              <div className="h-14 flex-shrink-0" style={{ width: "72px", overflow: "hidden" }}>
                <img src={logoIcon} alt="MAPA35 icono" className="h-14 max-w-none drop-shadow-lg" style={{ width: "auto" }} width={194} height={112} />
              </div>
              <span className="font-serif text-3xl font-bold tracking-tight text-white">MAPA<span className="text-[#2FB7B0]">35</span></span>
            </div>
            <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[11px] tracking-[0.2em] uppercase text-white/40 mb-8 pl-0.5">
              <span className="text-[#F7931E]">M</span><span>edición</span>
              <span className="text-white/20 mx-1">·</span>
              <span className="text-[#2BB673]">A</span><span>nálisis</span>
              <span className="text-white/20 mx-1">·</span>
              <span className="text-[#2FB7B0]">P</span><span>revención</span>
              <span className="text-white/20 mx-1">·</span>
              <span className="text-[#3B82F6]">A</span><span>cción</span>
            </div>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
            className="font-serif text-3xl sm:text-4xl lg:text-[3.25rem] font-bold leading-[1.4] text-white mb-6"
          >
            Protege a tu empresa ante inspecciones de la
            <span className="block mt-2 text-[#2FB7B0]">NOM-035</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15, ease: "easeOut" }}
            className="text-lg sm:text-xl text-white font-medium leading-relaxed mb-4 max-w-xl"
          >
            MAPA35 es la plataforma de cumplimiento NOM-035-STPS para empresas en México que convierte la evaluación de riesgo psicosocial en expedientes trazables, reportes ejecutivos y un plan de acción formal, listos para cualquier inspección de la STPS.
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
            className="text-base text-white/80 leading-relaxed mb-4 max-w-xl"
          >
            Implementamos un sistema de cumplimiento documentado que te respalda ante cualquier requerimiento de la STPS.
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3, ease: "easeOut" }}
            className="text-base text-white/80 mb-10 max-w-lg"
          >
            Plataforma que genera expedientes trazables, reportes ejecutivos y planes de acción formales. Listo para cualquier inspección de la STPS.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3, ease: "easeOut" }}
            className="flex flex-col sm:flex-row flex-wrap gap-3 mb-12"
          >
            <Button size="lg" onClick={() => scrollToContact("diagnostico_gratuito")} data-testid="button-hero-diagnostico">
              Solicitar diagnóstico gratuito
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button size="lg" variant="outline" className="bg-white/5 backdrop-blur-sm border-white/20 text-white" onClick={() => scrollToContact("hablar_especialista")} data-testid="button-hero-asesor">
              <Phone className="w-4 h-4 mr-2" />
              Hablar con un especialista
            </Button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="flex flex-col sm:flex-row flex-wrap gap-6"
          >
            {[
              { icon: Scale, label: "Respaldo legal sólido" },
              { icon: Layers, label: "Expedientes trazables" },
              { icon: Shield, label: "Prevención de sanciones" },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-2.5 text-sm text-white/70">
                <item.icon className="w-4 h-4 text-[#2FB7B0] flex-shrink-0" />
                <span>{item.label}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function ProblemaSection() {
  const riesgos = [
    { icon: Search, title: "Inspecciones sin aviso", text: "La STPS puede presentarse en cualquier momento. Si no tienes la documentación en orden, la multa es inmediata." },
    { icon: FileWarning, title: "Multas de hasta $535,000 MXN", text: "La falta de cumplimiento o documentación incompleta puede resultar en sanciones económicas severas por cada incumplimiento detectado." },
    { icon: FileMinus, title: "Requerimientos sin respaldo", text: "Cuando la autoridad solicita evidencia de acciones tomadas, un PDF genérico no es suficiente para demostrar cumplimiento." },
    { icon: AlertTriangle, title: "Evidencia desorganizada", text: "Tener la información dispersa o mal integrada es equivalente a no tenerla. La trazabilidad es clave ante una auditoría." },
    { icon: UserX, title: "Proveedores sin continuidad", text: "Muchos consultores entregan un reporte y desaparecen. Sin seguimiento, el cumplimiento queda expuesto." },
  ];

  return (
    <section id="problema" className="py-20 md:py-28">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection>
          <div className="max-w-3xl mx-auto text-center mb-16">
            <p className="text-sm font-medium text-destructive tracking-wider uppercase mb-3">El riesgo es real</p>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-6">
              La NOM-035 mal implementada puede costarte caro
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
              La mayoría de las empresas no temen aplicar la encuesta. Temen una inspección y descubrir que no están preparadas para demostrar cumplimiento.
            </p>
          </div>
        </AnimatedSection>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-14">
          {riesgos.map((riesgo, i) => (
            <AnimatedSection key={i} delay={i * 0.06}>
              <Card className="p-6 h-full">
                <div className="w-10 h-10 rounded-md bg-destructive/8 flex items-center justify-center mb-4">
                  <riesgo.icon className="w-5 h-5 text-destructive" />
                </div>
                <h3 className="font-serif font-semibold text-sm text-foreground mb-2">{riesgo.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{riesgo.text}</p>
              </Card>
            </AnimatedSection>
          ))}
        </div>

        <AnimatedSection delay={0.3}>
          <div className="max-w-2xl mx-auto">
            <Card className="p-6 bg-destructive/[0.03] border-destructive/10">
              <p className="font-serif text-base font-medium text-foreground text-center leading-relaxed">
                "Más de 250 empleados evaluados con metodología NOM-035 documentada y trazable."
              </p>
            </Card>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}

function CtaIntermedioSection() {
  const scrollToContacto = () => {
    document.getElementById("contacto")?.scrollIntoView({ behavior: "smooth" });
    trackEvent("click_cta", { event_category: "cta_intermedio", event_label: "autodiagnostico" });
  };

  return (
    <section className="py-14 md:py-20 bg-primary">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <AnimatedSection>
          <h2 className="font-serif text-2xl sm:text-3xl font-bold text-white mb-4">
            ¿Tu empresa cumple con la NOM-035? Descúbrelo en 2 minutos
          </h2>
          <p className="text-white/80 text-base mb-8 max-w-2xl mx-auto">
            Solicita un diagnóstico gratuito y conoce el estado real de cumplimiento de tu organización.
          </p>
          <Button
            size="lg"
            onClick={scrollToContacto}
            className="bg-white text-primary hover:bg-white/90 font-semibold px-8 py-3 text-base"
          >
            Hacer autodiagnóstico gratuito
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </AnimatedSection>
      </div>
    </section>
  );
}

function PromesaSection() {
  const features = [
    { icon: ClipboardCheck, title: "Evaluación normativa", text: "Encuestas de riesgo psicosocial conforme a los lineamientos oficiales de la NOM-035-STPS-2018" },
    { icon: FileText, title: "Expedientes integrados", text: "Documentación de cada trabajador y centro de trabajo en un solo lugar" },
    { icon: Eye, title: "Evidencia trazable", text: "Cada acción registrada con fecha, responsable y resultado" },
    { icon: BookOpen, title: "Plan de acción formal", text: "Acciones específicas, plazos y responsables definidos" },
    { icon: BarChart3, title: "Reportes ejecutivos", text: "Información clara y accionable para Dirección y RH" },
    { icon: Clock, title: "Seguimiento continuo", text: "Acompañamiento durante todo el ciclo para mantener el cumplimiento vigente" },
  ];

  return (
    <section id="promesa" className="py-20 md:py-28 bg-card/40">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection>
          <div className="max-w-3xl mx-auto text-center mb-14">
            <p className="text-sm font-medium text-primary tracking-wider uppercase mb-3">Nuestra solución</p>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-6">
              Una plataforma de cumplimiento NOM-035 documentado
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              MAPA35 es el software NOM-035 que construye el respaldo documental que tu empresa necesita para demostrar cumplimiento ante cualquier autoridad. No solo cumple con la norma — fortalece tu organización.
            </p>
          </div>
        </AnimatedSection>

        <AnimatedSection delay={0.15}>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {features.map((item, i) => (
              <Card key={i} className="p-5">
                <div className="w-10 h-10 rounded-md bg-primary/8 flex items-center justify-center mb-4">
                  <item.icon className="w-5 h-5 text-primary" />
                </div>
                <h3 className="font-serif font-semibold text-sm text-foreground mb-1.5">{item.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{item.text}</p>
              </Card>
            ))}
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}

function DiferenciacionSection() {
  const tradicional = [
    { text: "Aplica una encuesta genérica", detail: "Sin adaptación al contexto de tu empresa" },
    { text: "Entrega un reporte en PDF", detail: "Datos crudos sin interpretación ni estrategia" },
    { text: "Desaparece después de entregar", detail: "Sin seguimiento ni acompañamiento posterior" },
  ];

  const mapa35 = [
    { text: "Analiza resultados a profundidad", detail: "Interpretación experta de cada factor de riesgo" },
    { text: "Documenta cada acción tomada", detail: "Expediente completo con evidencia trazable" },
    { text: "Asesora para mejorar indicadores", detail: "Estrategias concretas para reducir riesgos" },
    { text: "Genera respaldo técnico formal", detail: "Documentación que resiste cualquier auditoría" },
    { text: "Mantiene trazabilidad anual", detail: "Seguimiento continuo del ciclo completo" },
  ];

  return (
    <section id="diferenciacion" className="py-20 md:py-28">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection>
          <div className="max-w-3xl mx-auto text-center mb-14">
            <p className="text-sm font-medium text-primary tracking-wider uppercase mb-3">La diferencia</p>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-6">
              Cumplir un trámite no es lo mismo que estar protegido
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              La diferencia entre un proveedor que solo entrega un PDF y un aliado que protege tu empresa es la calidad del respaldo documental.
            </p>
          </div>
        </AnimatedSection>

        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-12">
          <AnimatedSection delay={0.1}>
            <Card className="p-7 h-full bg-muted/30">
              <div className="flex items-center gap-3 mb-7">
                <div className="w-10 h-10 rounded-md bg-muted flex items-center justify-center">
                  <FileMinus className="w-5 h-5 text-muted-foreground" />
                </div>
                <div>
                  <h3 className="font-serif font-semibold text-base text-muted-foreground">Proveedor común</h3>
                  <p className="text-xs text-muted-foreground/80">Enfoque superficial</p>
                </div>
              </div>
              <div className="space-y-4">
                {tradicional.map((item, i) => (
                  <div key={i} className="flex gap-3">
                    <X className="w-4 h-4 text-muted-foreground/40 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm text-muted-foreground">{item.text}</p>
                      <p className="text-xs text-muted-foreground/80 mt-0.5">{item.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </AnimatedSection>

          <AnimatedSection delay={0.2}>
            <Card className="p-7 h-full border-primary/20 bg-primary/[0.02]">
              <div className="flex items-center gap-3 mb-7">
                <div className="w-10 h-10 rounded-md bg-primary flex items-center justify-center">
                  <Shield className="w-5 h-5 text-primary-foreground" />
                </div>
                <div>
                  <h3 className="font-serif font-semibold text-base text-primary">MAPA35</h3>
                  <p className="text-xs text-muted-foreground">Cumplimiento integral</p>
                </div>
              </div>
              <div className="space-y-4">
                {mapa35.map((item, i) => (
                  <div key={i} className="flex gap-3">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm text-foreground">{item.text}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{item.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </AnimatedSection>
        </div>

        <AnimatedSection delay={0.3}>
          <p className="text-center font-serif text-base font-medium text-muted-foreground">
            Saber cómo cumplir la NOM-035 no es tener un documento. Es contar con un <span className="text-foreground">sistema de prevención estructurada.</span>
          </p>
        </AnimatedSection>
      </div>
    </section>
  );
}


// TODO: Conectar al API real cuando el endpoint exista
// URL base: https://nom035-api.sitilab.net/
// Endpoint esperado: GET /stats (o similar)
// Respuesta esperada: { empleadosEvaluados: number, empresasAtendidas: number, cumplimientoPromedio: number }
const STATS_PLACEHOLDER = {
  empleadosEvaluados: 1500,
  empresasAtendidas: 45,
  cumplimientoPromedio: 96,
};

function StatsSection() {
  // TODO: Reemplazar placeholder con fetch real al API
  // const { data: stats } = useQuery({ queryKey: ["/api/stats"], queryFn: async () => {
  //   const res = await fetch("https://nom035-api.sitilab.net/stats");
  //   return res.json();
  // }});
  const stats = STATS_PLACEHOLDER;

  const items = [
    { value: stats.empleadosEvaluados.toLocaleString("es-MX"), label: "Empleados evaluados" },
    { value: stats.empresasAtendidas.toLocaleString("es-MX"), label: "Empresas atendidas" },
    { value: `${stats.cumplimientoPromedio}%`, label: "Cumplimiento logrado" },
  ];

  return (
    <section className="py-14 md:py-20 bg-primary/5">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection>
          <div className="grid grid-cols-3 gap-6 text-center">
            {items.map((item, i) => (
              <div key={i}>
                <p className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-primary mb-2">{item.value}</p>
                <p className="text-xs sm:text-sm text-muted-foreground">{item.label}</p>
              </div>
            ))}
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}

function ProcesoSection() {
  const pasos = [
    { step: "01", title: "Diagnóstico inicial", description: "Evaluamos el estado actual de cumplimiento y los riesgos específicos de tu organización", icon: Search },
    { step: "02", title: "Evaluación estructurada", description: "Aplicamos los instrumentos normativos conforme a los lineamientos oficiales de la STPS", icon: ClipboardCheck },
    { step: "03", title: "Integración de expediente", description: "Organizamos toda la evidencia documental en expedientes completos y trazables", icon: FileText },
    { step: "04", title: "Plan de acción", description: "Diseñamos un plan formal con acciones concretas, plazos y responsables definidos", icon: BookOpen },
    { step: "05", title: "Implementación guiada", description: "Te acompañamos en la ejecución de cada acción, asegurándonos de que quede documentada", icon: Target },
    { step: "06", title: "Seguimiento anual", description: "Mantenemos el ciclo activo con revisiones periódicas y actualizaciones del expediente", icon: Clock },
  ];

  return (
    <section id="proceso" className="py-20 md:py-28">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection>
          <div className="max-w-3xl mx-auto text-center mb-16">
            <p className="text-sm font-medium text-primary tracking-wider uppercase mb-3">Metodología</p>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-6">
              Un proceso probado en 6 etapas
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Cada etapa está diseñada para generar evidencia documental que respalde el cumplimiento de tu empresa.
            </p>
          </div>
        </AnimatedSection>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {pasos.map((paso, i) => (
            <AnimatedSection key={i} delay={i * 0.06}>
              <Card className="p-6 h-full">
                <div className="flex items-center gap-3 mb-4">
                  <span className="font-serif text-3xl font-bold text-primary/15 leading-none">{paso.step}</span>
                  <div className="w-9 h-9 rounded-md bg-primary/8 flex items-center justify-center">
                    <paso.icon className="w-4.5 h-4.5 text-primary" />
                  </div>
                </div>
                <h3 className="font-serif font-semibold text-sm text-foreground mb-2">{paso.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{paso.description}</p>
              </Card>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
}

const FAQ_ITEMS = [
  {
    question: "¿Qué es la NOM-035-STPS?",
    answer:
      "La NOM-035-STPS-2018 es la norma oficial mexicana que establece la obligación de identificar, analizar y prevenir los factores de riesgo psicosocial en los centros de trabajo, así como promover un entorno organizacional favorable.",
  },
  {
    question: "¿A qué empresas aplica la NOM-035?",
    answer:
      "Aplica a todos los centros de trabajo en México. El nivel de obligaciones —desde identificar factores de riesgo hasta implementar evaluaciones y medidas de control— crece según el número de trabajadores del centro de trabajo.",
  },
  {
    question: "¿Qué pasa si mi empresa no cumple con la NOM-035?",
    answer:
      "La STPS puede observar y sancionar el incumplimiento durante una inspección, y la falta de evidencia documental deja a la empresa expuesta ante demandas laborales relacionadas con el entorno de trabajo. MAPA35 genera esa evidencia de forma trazable para que tu empresa pueda demostrar cumplimiento en cualquier momento.",
  },
  {
    question: "¿Cómo es el proceso de implementación de MAPA35?",
    answer:
      "MAPA35 acompaña a tu empresa en 6 etapas: diagnóstico inicial, evaluación estructurada con los instrumentos oficiales de la STPS, integración del expediente documental, diseño del plan de acción, implementación guiada y seguimiento anual.",
  },
  {
    question: "¿El diagnóstico inicial tiene costo?",
    answer:
      "No. El diagnóstico inicial de MAPA35 es gratuito y sirve para evaluar el estado de cumplimiento actual de tu empresa antes de definir cualquier plan de acción.",
  },
];

function FaqSection() {
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ_ITEMS.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };

  return (
    <section id="faq" className="py-20 md:py-28 bg-card">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection>
          <div className="text-center mb-12">
            <p className="text-sm font-medium text-primary tracking-wider uppercase mb-3">Preguntas frecuentes</p>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground">
              Lo que las empresas preguntan sobre la NOM-035
            </h2>
          </div>
        </AnimatedSection>

        <AnimatedSection delay={0.1}>
          <div className="divide-y divide-border">
            {FAQ_ITEMS.map((item) => (
              <details key={item.question} className="group py-5">
                <summary className="flex items-center justify-between gap-4 cursor-pointer list-none font-serif font-semibold text-foreground">
                  {item.question}
                  <ChevronRight className="w-4 h-4 flex-shrink-0 text-primary transition-transform duration-200 group-open:rotate-90" />
                </summary>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                  {item.answer}
                </p>
              </details>
            ))}
          </div>
        </AnimatedSection>
      </div>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />
    </section>
  );
}

const contactFormSchema = insertContactRequestSchema.extend({
  name: z.string().min(2, "Ingresa tu nombre completo"),
  company: z.string().min(2, "Ingresa el nombre de tu empresa"),
  email: z.string().email("Ingresa un correo electrónico válido"),
  phone: z.string().min(7, "Ingresa un número de teléfono válido"),
});

function CierreSection() {
  const { toast } = useToast();
  const [showCalendly, setShowCalendly] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [prefill, setPrefill] = useState<{ name?: string; email?: string; phone?: string; company?: string }>({});
  const utmRef = useRef<Record<string, string>>({});

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const utm: Record<string, string> = {};
    for (const key of ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "gclid"]) {
      const val = params.get(key);
      if (val) utm[key] = val;
    }
    utmRef.current = utm;
  }, []);

  const form = useForm<InsertContactRequest>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      name: "",
      company: "",
      email: "",
      phone: "",
    },
  });

  const mutation = useMutation({
    mutationFn: async (data: InsertContactRequest) => {
      const res = await apiRequest("POST", "/api/contact", data);
      return res.json();
    },
    onSuccess: (_res, variables) => {
      trackEvent("generate_lead", { event_category: "contact_form", event_label: "diagnostico_gratuito" });
      const phone = variables.phone
        ? variables.phone.startsWith("+") ? variables.phone : `+52${variables.phone}`
        : undefined;
      setPrefill({ name: variables.name, email: variables.email, phone, company: variables.company });
      form.reset();
      setFormSubmitted(true);
      setShowCalendly(true);
    },
    onError: () => {
      toast({
        title: "No pudimos enviar tu solicitud",
        description: "Intenta de nuevo o escríbenos directamente a mapa35@concilya.com",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: InsertContactRequest) => {
    const hp = (document.querySelector("input[name='_hp']") as HTMLInputElement)?.value ?? "";
    mutation.mutate({ ...data, ...utmRef.current, _hp: hp } as any);
  };

  return (
    <>
    {showCalendly && <CalendlyModal onClose={() => setShowCalendly(false)} prefill={prefill} />}
    <section id="contacto" className="py-20 md:py-28 bg-card/40">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-14 md:gap-20 items-start">
          <AnimatedSection>
            <p className="text-sm font-medium text-primary tracking-wider uppercase mb-3">Da el primer paso</p>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-4">
              La NOM-035 no debe ser un riesgo para tu negocio
            </h2>
            <p className="text-xl font-serif font-semibold text-primary mb-6">
              Debe estar bajo control.
            </p>
            <p className="text-base text-muted-foreground leading-relaxed mb-10">
              Solicita un diagnóstico sin costo para conocer el estado actual de cumplimiento de tu empresa ante los requisitos de inspección STPS NOM-035. Nuestro equipo evaluará tu situación y te presentará un plan de acción claro.
            </p>

            <div className="space-y-5">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-md bg-primary/8 flex items-center justify-center flex-shrink-0">
                  <Briefcase className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">Diagnóstico sin compromiso</p>
                  <p className="text-xs text-muted-foreground">Evaluamos tu situación antes de cualquier propuesta</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-md bg-primary/8 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">Respuesta en 24 horas hábiles</p>
                  <p className="text-xs text-muted-foreground">Un especialista te contactará para agendar tu diagnóstico</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-md bg-primary/8 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">mapa35@concilya.com</p>
                  <p className="text-xs text-muted-foreground">Si prefieres escribirnos directamente</p>
                </div>
              </div>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={0.15}>
            <Card className="p-6 sm:p-8">
              {formSubmitted ? (
                <div className="text-center py-6 space-y-5">
                  <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                    <BadgeCheck className="w-7 h-7 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-serif text-xl font-semibold text-foreground mb-2">
                      ¡Recibimos tu solicitud!
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-2">
                      Un especialista en NOM-035 te contactará en las próximas <span className="font-medium text-foreground">24 horas hábiles</span> para agendar tu diagnóstico gratuito.
                    </p>
                    <p className="text-xs text-muted-foreground/80">
                      Revisa tu bandeja de entrada (y spam) para nuestra confirmación.
                    </p>
                  </div>
                  {/* TODO: Habilitar cuando el PDF del checklist esté disponible */}
                  {/* <div className="border-t border-border pt-5">
                    <p className="text-sm font-medium text-foreground mb-3">Mientras tanto, descarga gratis:</p>
                    <a
                      href="/checklist-nom-035.pdf"
                      download
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary/8 hover:bg-primary/15 text-primary text-sm font-medium rounded-md transition-colors"
                      onClick={() => trackEvent("download", { event_category: "lead_magnet", event_label: "checklist_nom035" })}
                    >
                      <Download className="w-4 h-4" />
                      Checklist de cumplimiento NOM-035 (PDF)
                    </a>
                  </div> */}
                </div>
              ) : (
                <>
                  <h3 className="font-serif text-lg font-semibold text-foreground mb-1">
                    Solicitar diagnóstico gratuito
                  </h3>
                  <p className="text-sm text-muted-foreground mb-2">
                    Completa tus datos y un especialista en NOM-035 te contactará.
                  </p>
                  <p className="text-sm text-primary/80 mb-6">
                    Recibirás un análisis personalizado del estado de cumplimiento de tu empresa con recomendaciones concretas.
                  </p>

                  <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" data-testid="form-contact">
                      {/* Honeypot: oculto para humanos, visible para bots */}
                      <input type="text" name="_hp" defaultValue="" aria-hidden="true" tabIndex={-1} style={{ position: "absolute", opacity: 0, pointerEvents: "none", width: 0, height: 0 }} />
                      <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Nombre completo</FormLabel>
                            <FormControl>
                              <Input placeholder="Ej. María González López" {...field} data-testid="input-name" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="company"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Empresa</FormLabel>
                            <FormControl>
                              <Input placeholder="Ej. Grupo Industrial del Norte S.A. de C.V." {...field} data-testid="input-company" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Correo corporativo</FormLabel>
                            <FormControl>
                              <Input type="email" placeholder="nombre@empresa.com.mx" {...field} data-testid="input-email" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="phone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Teléfono de contacto</FormLabel>
                            <FormControl>
                              <Input type="tel" placeholder="(000) 000 0000" {...field} data-testid="input-phone" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <div className="flex items-center gap-3">
                        <Button type="submit" className="flex-1" disabled={mutation.isPending} data-testid="button-submit-contact">
                          {mutation.isPending ? "Enviando solicitud..." : "Obtener mi diagnóstico gratuito"}
                          {!mutation.isPending && <ArrowRight className="w-4 h-4 ml-2" />}
                        </Button>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">Agenda en 30s</span>
                      </div>
                      <div className="flex items-center justify-center gap-1.5 pt-1">
                        <Lock className="w-3 h-3 text-muted-foreground/80" />
                        <p className="text-xs text-muted-foreground/80">
                          Tu información es confidencial y no la compartimos con terceros.
                        </p>
                      </div>
                    </form>
                  </Form>
                </>
              )}
            </Card>
          </AnimatedSection>
        </div>
      </div>
    </section>
    </>
  );
}

function Footer() {
  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer className="py-10 border-t">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row flex-wrap items-center justify-between gap-6">
          <div className="flex items-center">
            <img src={logoFullColor} alt="MAPA35" className="h-7 w-auto" width={255} height={72} loading="lazy" />
          </div>
          <div className="flex flex-wrap items-center justify-center gap-6">
            <button onClick={() => scrollTo("problema")} className="text-xs text-muted-foreground transition-colors" data-testid="link-footer-problema">Riesgos</button>
            <button onClick={() => scrollTo("promesa")} className="text-xs text-muted-foreground transition-colors" data-testid="link-footer-promesa">Solución</button>
            <button onClick={() => scrollTo("proceso")} className="text-xs text-muted-foreground transition-colors" data-testid="link-footer-proceso">Proceso</button>
            <button onClick={() => scrollTo("contacto")} className="text-xs text-muted-foreground transition-colors" data-testid="link-footer-contacto">Contacto</button>
            <Link href="/faq" className="text-xs text-muted-foreground transition-colors" data-testid="link-footer-faq">FAQ</Link>
            <Link href="/glosario" className="text-xs text-muted-foreground transition-colors" data-testid="link-footer-glosario">Glosario</Link>
            <Link href="/blog" className="text-xs text-muted-foreground transition-colors" data-testid="link-footer-blog">Blog</Link>
          </div>
          <p className="text-xs text-muted-foreground/80">
            Software de cumplimiento NOM-035-STPS profesional — Guía cumplimiento NOM-035 2026
          </p>
        </div>
      </div>
    </footer>
  );
}

// TODO: Reemplazar con el número real de WhatsApp
const WHATSAPP_NUMBER = "5215621212282";
const WHATSAPP_MESSAGE = "Hola, me interesa un diagnóstico de cumplimiento NOM-035 para mi empresa.";

function WhatsAppButton() {
  const href = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`;

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Contactar por WhatsApp"
      onClick={() => trackEvent("click_cta", { event_category: "whatsapp", event_label: "boton_flotante" })}
      className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#25D366] hover:bg-[#20BD5A] hover:scale-110 rounded-full flex items-center justify-center shadow-lg transition-all duration-200"
    >
      <svg viewBox="0 0 32 32" className="w-7 h-7 fill-white">
        <path d="M16.004 0C7.165 0 .004 7.161.004 16c0 2.822.736 5.584 2.137 8.012L.027 32l8.2-2.148A15.916 15.916 0 0 0 16.004 32C24.839 32 32 24.839 32 16S24.839 0 16.004 0zm0 29.116a13.067 13.067 0 0 1-6.662-1.82l-.477-.284-4.95 1.298 1.322-4.827-.312-.496a13.04 13.04 0 0 1-2.001-6.987c0-7.214 5.872-13.082 13.08-13.082 7.214 0 13.082 5.868 13.082 13.082 0 7.208-5.868 13.116-13.082 13.116zm7.17-9.8c-.393-.196-2.326-1.147-2.687-1.278-.36-.131-.623-.196-.886.197-.262.393-1.016 1.278-1.245 1.54-.23.263-.459.296-.852.099-.393-.196-1.66-.612-3.163-1.95-1.17-1.042-1.959-2.328-2.188-2.72-.23-.394-.025-.607.172-.803.178-.176.393-.459.59-.689.196-.23.262-.393.393-.656.131-.262.065-.492-.033-.689-.098-.196-.886-2.135-1.213-2.923-.32-.766-.644-.663-.886-.675-.23-.012-.492-.014-.755-.014s-.689.098-1.049.492c-.36.393-1.376 1.344-1.376 3.28s1.41 3.804 1.606 4.066c.197.262 2.774 4.234 6.72 5.937.94.405 1.672.647 2.243.828.943.3 1.8.257 2.479.156.756-.113 2.326-.951 2.654-1.87.328-.918.328-1.704.23-1.87-.099-.163-.362-.262-.755-.459z" />
      </svg>
    </a>
  );
}

export default function Landing() {
  useEffect(() => {
    captureTrackingParams();
    if (window.location.hash) {
      const id = window.location.hash.slice(1);
      requestAnimationFrame(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      });
    }
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <HeroSection />
      <ProblemaSection />
      <CtaIntermedioSection />
      <PromesaSection />
      <DiferenciacionSection />
      {/* TODO: Habilitar cuando el API de stats esté listo */}
      {/* <StatsSection /> */}
      <ProcesoSection />
      <FaqSection />
      <CierreSection />
      <Footer />
      <WhatsAppButton />
      <ChatbotWidget />
    </div>
  );
}
